/**
 * Column Dropdown Filter Component
 *
 * Excel-like dropdown filter with distinct values:
 * - Shows all unique values in a column
 * - Multi-select with checkboxes
 * - "Select All" / "Deselect All" functionality
 * - Search within dropdown values
 * - Special "(Tom)" option for date columns to filter NULL values via text filter
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown,
  Check,
  Search,
  X
} from 'lucide-react';
import type { ColumnDefinition, TableDataRow } from '../types/table';

interface ColumnDropdownFilterProps<T extends TableDataRow = TableDataRow> {
  column: ColumnDefinition<T>;
  data: T[];
  selectedValues: string[];
  onSelectionChange: (selectedValues: string[]) => void;
  formatValue?: (value: any, row: T) => string;
  /** Optional callback to set the text filter value (used for special options like "(Tom)" in server-side mode) */
  onTextFilterSet?: (columnKey: string, value: string) => void;
  /** Current text filter value (to show active state for special options) */
  textFilterValue?: string;
  /** B5: vis «(Tom)»/«(Ikke tom)»-valg også for ikke-dato-kolonner (krever backend-støtte for tom/!tom) */
  enableEmptyFilters?: boolean;
}

export const ColumnDropdownFilter = <T extends TableDataRow = TableDataRow>({
  column,
  data,
  selectedValues,
  onSelectionChange,
  formatValue,
  onTextFilterSet,
  textFilterValue,
  enableEmptyFilters = false
}: ColumnDropdownFilterProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isDateColumn = column.type === 'date';
  // Token «(tom)»/«(ikke tom)» er utvetydig (unngår kollisjon med fritekst-søk på ordet «tom»).
  // «tom»/«!tom» beholdes som gyldig aktiv-tilstand for bakoverkompatibilitet (datofilter).
  const _tfv = textFilterValue?.toLowerCase();
  const isNullFilterActive = _tfv === '(tom)' || _tfv === 'tom';
  const isNotNullFilterActive = _tfv === '(ikke tom)' || _tfv === '!tom';
  // B5: «(Tom)» vises for datokolonner (uendret oppførsel) eller når enableEmptyFilters er på.
  // «(Ikke tom)» vises kun ved opt-in (enableEmptyFilters) — unngår nytt valg på tabeller som
  // ikke har bekreftet backend-støtte (f.eks. TransactionHistory beholder dagens dato-«(Tom)»).
  const showEmptyOptions = !!onTextFilterSet && (isDateColumn || enableEmptyFilters);
  const showNotTomOption = !!onTextFilterSet && enableEmptyFilters;

  // Get distinct values for this column with both raw and formatted values.
  // #2 oppslagsfelter: når kolonnen har `filterOptions` (kildeliste fra f.eks.
  // statuskoder/filtergrupper) vises HELE den gyldige listen — også verdier som
  // ikke finnes i de synlige dataene. Ellers utledes distinct fra dataene som før.
  const distinctValues = useMemo(() => {
    if (column.filterOptions && column.filterOptions.length > 0) {
      return column.filterOptions.map(opt => ({
        rawValue: String(opt.value),
        formattedValue: opt.label
      }));
    }

    const valueMap = new Map<string, string>(); // rawValue -> formattedValue

    data.forEach(item => {
      const value = item[column.key];
      if (value !== null && value !== undefined) {
        const rawValue = String(value);
        const formattedValue = formatValue ? formatValue(value, item) : rawValue;
        if (formattedValue.trim()) {
          valueMap.set(rawValue, formattedValue);
        }
      }
    });

    // Sort by formatted value but keep the mapping
    const sortedEntries = Array.from(valueMap.entries()).sort(([, a], [, b]) => {
      // Sort numbers numerically, strings alphabetically
      if (column.type === 'number') {
        return Number(a) - Number(b);
      }
      return a.localeCompare(b, 'no-NO');
    });

    return sortedEntries.map(([rawValue, formattedValue]) => ({
      rawValue,
      formattedValue
    }));
  }, [data, column.key, column.type, column.filterOptions, formatValue]);

  // Check if there are any null values in the data for this column
  const hasNullValues = useMemo(() => {
    if (!isDateColumn) return false;
    return data.some(item => item[column.key] === null || item[column.key] === undefined);
  }, [data, column.key, isDateColumn]);

  // Filter distinct values based on search term
  const filteredValues = useMemo(() => {
    if (!searchTerm.trim()) return distinctValues;
    const searchLower = searchTerm.toLowerCase();
    return distinctValues.filter(item =>
      item.formattedValue.toLowerCase().includes(searchLower)
    );
  }, [distinctValues, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside the trigger button and not inside any dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        // If we clicked inside a dropdown (any element with data-dropdown-content), don't close
        if (!(target as Element).closest('[data-dropdown-content="true"]')) {
          setIsOpen(false);
          setSearchTerm('');
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggleValue = (rawValue: string) => {
    // B5: velge en verdi opphever et aktivt tom/ikke-tom-filter (de er gjensidig utelukkende)
    if ((isNullFilterActive || isNotNullFilterActive) && onTextFilterSet) {
      onTextFilterSet(String(column.key), '');
    }
    const newSelection = selectedValues.includes(rawValue)
      ? selectedValues.filter(v => v !== rawValue)
      : [...selectedValues, rawValue];

    onSelectionChange(newSelection);
  };

  const handleSelectAll = () => {
    onSelectionChange(filteredValues.map(item => item.rawValue));
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  // B5: aktiver/deaktiver «(Tom)» eller «(Ikke tom)». Opphever verdivalg når aktivert.
  const handleEmptyFilterClick = (kind: 'empty' | 'nonempty') => {
    if (!onTextFilterSet) return;
    const active = kind === 'empty' ? isNullFilterActive : isNotNullFilterActive;
    // Datokolonner beholder bar «tom»/«!tom» (bakoverkompatibelt med eksisterende datofilter).
    // Ikke-dato bruker utvetydig «(tom)»/«(ikke tom)» for å unngå fritekst-kollisjon.
    const token = kind === 'empty'
      ? (isDateColumn ? 'tom' : '(tom)')
      : (isDateColumn ? '!tom' : '(ikke tom)');
    onTextFilterSet(String(column.key), active ? '' : token);
    if (!active && selectedValues.length > 0) {
      onSelectionChange([]); // tom/ikke-tom er gjensidig utelukkende med verdivalg
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const isAllSelected = filteredValues.length > 0 &&
    filteredValues.every(item => selectedValues.includes(item.rawValue));

  const isSomeSelected = selectedValues.length > 0 && selectedValues.length < filteredValues.length;
  const hasActiveFilter = selectedValues.length > 0;
  const hasAnyActiveFilter = hasActiveFilter || isNullFilterActive || isNotNullFilterActive;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger Button */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`p-0.5 rounded transition-colors relative ${
          hasAnyActiveFilter ? 'text-teal-600 bg-teal-50' : 'text-blue-200 hover:text-white'
        }`}
        title={hasAnyActiveFilter ? `Filter aktiv: ${selectedValues.length} valgt` : `Filter ${column.label}`}
      >
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        {hasAnyActiveFilter && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-teal-500 rounded-full ring-1 ring-white" />
        )}
      </button>

      {/* Dropdown Menu - Portal to body to ensure highest stacking context */}
      {isOpen && createPortal(
        <div
          className="fixed w-64 bg-white border border-gray-300 rounded-lg shadow-xl max-h-80 flex flex-col"
          data-dropdown-content="true"
          style={{
            zIndex: 2147483647,
            position: 'fixed',
            top: dropdownRef.current ?
              dropdownRef.current.getBoundingClientRect().bottom + 4 : 0,
            left: dropdownRef.current ?
              Math.max(8, dropdownRef.current.getBoundingClientRect().right - 256) : 0
          }}
        >
          {/* Header with search */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Søk verdier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Select All / Deselect All */}
          <div className="p-2 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between text-sm">
              <button
                onClick={handleSelectAll}
                disabled={isAllSelected}
                className="text-teal-600 hover:text-teal-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Velg alle
              </button>
              <button
                onClick={handleDeselectAll}
                disabled={selectedValues.length === 0}
                className="text-red-600 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Fjern alle
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {selectedValues.length} av {distinctValues.length} valgt
            </div>
          </div>

          {/* B5: «(Tom)» / «(Ikke tom)» — filtrer på manglende/eksisterende verdi.
              Vises for datokolonner (alltid) eller når enableEmptyFilters er på. */}
          {showEmptyOptions && (
            <div className="border-b border-gray-200">
              <label
                className={`flex items-center px-3 py-2 cursor-pointer text-sm ${
                  isNullFilterActive ? 'bg-teal-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => handleEmptyFilterClick('empty')}
              >
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={isNullFilterActive}
                    onChange={() => handleEmptyFilterClick('empty')}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                </div>
                <span className="ml-2 flex-1 text-gray-600 italic">
                  (Tom){isDateColumn ? ' — uten dato' : ' — uten verdi'}
                </span>
              </label>
              {showNotTomOption && (
                <label
                  className={`flex items-center px-3 py-2 cursor-pointer text-sm ${
                    isNotNullFilterActive ? 'bg-teal-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleEmptyFilterClick('nonempty')}
                >
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={isNotNullFilterActive}
                      onChange={() => handleEmptyFilterClick('nonempty')}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                  </div>
                  <span className="ml-2 flex-1 text-gray-600 italic">
                    (Ikke tom){isDateColumn ? ' — med dato' : ' — med verdi'}
                  </span>
                </label>
              )}
            </div>
          )}

          {/* Values List */}
          <div className="flex-1 overflow-y-auto">
            {filteredValues.length > 0 ? (
              filteredValues.map((item) => (
                <label
                  key={item.rawValue}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                >
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(item.rawValue)}
                      onChange={() => handleToggleValue(item.rawValue)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    {selectedValues.includes(item.rawValue) && (
                      <Check className="absolute inset-0 w-4 h-4 text-white pointer-events-none" style={{ fontSize: '12px' }} />
                    )}
                  </div>
                  <span className="ml-2 flex-1 truncate" title={item.formattedValue}>
                    {item.formattedValue}
                  </span>
                </label>
              ))
            ) : (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                Ingen verdier funnet
              </div>
            )}
          </div>

          {/* Footer */}
          {hasAnyActiveFilter && (
            <div className="p-2 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  // Tøm kun det aktive (verdivalg ELLER tom/ikke-tom — de er gjensidig utelukkende),
                  // så det blir ett enkelt filter-kall og ny henting kjøres rent.
                  if ((isNullFilterActive || isNotNullFilterActive) && onTextFilterSet) {
                    onTextFilterSet(String(column.key), '');
                  } else {
                    onSelectionChange([]);
                  }
                }}
                className="w-full px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 inline-flex items-center justify-center gap-1"
              >
                <X className="w-3.5 h-3.5" />
                Fjern filter
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};
